import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertService } from '../alert.service';
import { environment } from '../../../environments/environment';

/**
 * Requisições que tratam o erro por conta própria (ex.: UploadService, que
 * exibe feedback específico) marcam este token para suprimir o alerta genérico.
 */
export const SKIP_ERROR_ALERT = new HttpContextToken<boolean>(() => false);

/**
 * Feedback genérico para erros HTTP REST. Este interceptor existia mas nunca
 * foi registrado — erros de upload passavam sem nenhum aviso ao usuário.
 *
 * Fora do escopo: GraphQL (o errorLink do Apollo já exibe Swal); 401 (o
 * authInterceptor é o INTERNO da cadeia — primeiro do array = mais externo —
 * e resolve o 401 com refresh+retry antes de o erro chegar aqui).
 */
export const errorInterceptor: HttpInterceptorFn = (
    req: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
    const alertService = inject(AlertService);

    const isGraphql = req.url.startsWith(environment.base_graphql);
    if (isGraphql || req.context.get(SKIP_ERROR_ALERT)) {
        return next(req);
    }

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                alertService.showMessage('Ops!', buildMessage(error), 'error');
            }
            return throwError(() => error);
        })
    );
};

function buildMessage(error: HttpErrorResponse): string {
    // Só mensagens de regra de negócio chegam prontas em pt-BR; a API não tem
    // exception filter global, então 404/500/413 trazem texto técnico em
    // inglês do próprio Nest (ex.: 'Cannot POST /...') — usa o fallback.
    if ([400, 403, 422].includes(error.status)) {
        const backendMessage = error.error?.message;
        const text = Array.isArray(backendMessage) ? backendMessage[0] : backendMessage;
        if (typeof text === 'string' && text.trim()) {
            return text;
        }
    }
    switch (error.status) {
        case 0: return 'Não foi possível conectar ao servidor. Verifique sua internet ou tente mais tarde.';
        case 403: return 'Você não tem permissão para acessar este recurso.';
        case 404: return 'Recurso não encontrado.';
        case 413: return 'O arquivo enviado é muito grande.';
        case 500: return 'Erro interno no servidor. Nossa equipe já foi notificada.';
        default: return `Ocorreu um erro inesperado (código ${error.status}). Tente novamente.`;
    }
}
